import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCheckRuns, runClientCheck } from '@/lib/api';
import { ALERTS_QUERY_KEY, CHECK_RUNS_QUERY_KEY, OVERVIEW_QUERY_KEY } from '@/lib/query-keys';

export { CHECK_RUNS_QUERY_KEY } from '@/lib/query-keys';

/**
 * How many runs the evidence drawer asks for: the current one plus three for history.
 *
 * @since 1.0.0
 */
export const DRAWER_RUN_LIMIT = 4;

/**
 * Reads recent runs of one check for one client, newest first, without throwing on failure.
 *
 * @since 1.0.0
 * @param clientSlug - client slug, or undefined to leave the query idle
 * @param checkSlug - check slug, or undefined to leave the query idle
 * @param limit - how many runs to read
 * @returns the check runs query result
 */
export function useCheckRuns(
    clientSlug: string | undefined,
    checkSlug: string | undefined,
    limit: number = DRAWER_RUN_LIMIT,
) {
    return useQuery({
        queryKey: [CHECK_RUNS_QUERY_KEY, clientSlug ?? null, checkSlug ?? null, limit],
        queryFn: () => getCheckRuns(clientSlug ?? '', checkSlug ?? '', limit),
        enabled: clientSlug !== undefined && checkSlug !== undefined,
        throwOnError: false,
    });
}

/**
 * Runs one check for one client now, then refreshes the run history, the overview dots and
 * the alert list the run may have changed.
 *
 * @since 1.5.0
 * @returns the run-now mutation
 */
export function useRunClientCheck() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ clientSlug, checkSlug }: { clientSlug: string; checkSlug: string }) =>
            runClientCheck(clientSlug, checkSlug),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CHECK_RUNS_QUERY_KEY] });
            await queryClient.invalidateQueries({ queryKey: OVERVIEW_QUERY_KEY });
            await queryClient.invalidateQueries({ queryKey: [ALERTS_QUERY_KEY] });
        },
    });
}
